import { ref, get, set, update } from 'firebase/database';
import { database } from '../firebase';
import { encodeIP } from '../utils/ipUtils';

export class ScoresAdapter {
    static API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
    
    static async updateScore(country, action, ip) {
        try {
            // Calculate score change
            const scoreChange = action === 'PRAY' ? 1 : -1;
            
            // Get current score
            const countryRef = ref(database, `countryScores/${country}`);
            const snapshot = await get(countryRef);
            const currentScore = snapshot.exists() ? snapshot.val() : 0;
            
            // Update score as a simple number (not an object)
            await set(countryRef, currentScore + scoreChange);
            
            // Store lastUpdate in a separate collection to avoid path conflicts
            const timestampsRef = ref(database, `countryTimestamps/${country}`);
            await set(timestampsRef, Date.now());
            
            // Also update the user's individual score if IP is provided
            if (ip) {
                const encodedIP = encodeIP(ip);
                const userRef = ref(database, `users/${encodedIP}`);
                const userSnapshot = await get(userRef);
                
                if (userSnapshot.exists()) {
                    const userData = userSnapshot.val();
                    await set(userRef, {
                        ...userData,
                        score: userData.score + scoreChange,
                        lastActive: Date.now(),
                        [action.toLowerCase() + 'Count']: userData[action.toLowerCase() + 'Count'] + 1
                    });
                }
            }
            
            // Add to score history via backend API
            try {
                await fetch(`${this.API_BASE}/scores/history`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ 
                        country, 
                        action,
                        timestamp: Date.now(),
                        scoreChange
                    })
                });
            } catch (historyError) {
                // Non-critical error, just log it
                console.warn('Error updating score history:', historyError);
            }
            
            return {
                success: true,
                newScore: currentScore + scoreChange
            };
        } catch (error) {
            console.error('Error updating score:', error);
            throw error;
        }
    }

    static async getScores() {
        try {
            const countryScoresRef = ref(database, 'countryScores');
            const snapshot = await get(countryScoresRef);
            const scores = snapshot.val() || {};
            
            // Filter out non-numeric values
            const filteredScores = {};
            Object.entries(scores).forEach(([key, value]) => {
                if (typeof value === 'number') {
                    filteredScores[key] = value;
                }
            });
            
            // Calculate global score
            const globalScore = Object.values(filteredScores)
                .reduce((sum, score) => sum + score, 0);
            
            return {
                success: true,
                globalScore,
                countryScores: filteredScores
            };
        } catch (error) {
            console.error('Error getting scores:', error);
            throw error;
        }
    }
} 